/**
 * Sequencing Activity
 * Students drag cards into the correct chronological order on a timeline
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { CheckCircle, XCircle, GripVertical, ArrowRight, ArrowUp, ListOrdered, ArrowDown, Calendar, Footprints, ArrowUpDown } from 'lucide-react';
import { motion } from 'motion/react';
import type { ActivitySlideProps } from '../types';
import { getDndBackend } from './dndBackend';

// Helper function to strip HTML tags from rich text content
function stripHtml(html: string): string {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
}

export type SequenceMode = 'custom' | 'chronological' | 'steps' | 'smallest-to-largest';

const SEQUENCE_MODE_CONFIG: Record<SequenceMode, { label: string; icon: typeof ListOrdered; dropPlaceholder: string; badgeColor: string }> = {
  custom: {
    label: 'Sequencing',
    icon: ListOrdered,
    dropPlaceholder: 'Drop cards here in order — first to last',
    badgeColor: 'bg-indigo-500/10 text-indigo-600',
  },
  chronological: {
    label: 'Chronological Order',
    icon: Calendar,
    dropPlaceholder: 'Drop cards here — earliest to latest',
    badgeColor: 'bg-amber-500/10 text-amber-600',
  },
  steps: {
    label: 'Steps in Order',
    icon: Footprints,
    dropPlaceholder: 'Drop cards here — first step to last step',
    badgeColor: 'bg-emerald-500/10 text-emerald-600',
  },
  'smallest-to-largest': {
    label: 'Smallest to Largest',
    icon: ArrowUpDown,
    dropPlaceholder: 'Drop cards here — smallest to largest',
    badgeColor: 'bg-purple-500/10 text-purple-600',
  },
};

interface SequenceItem {
  id: string;
  label: string;
  description: string;
}

const DRAG_TYPE = 'SEQUENCE_ITEM';

/* ------------------------------------------------------------------ */
/*  Draggable card component                                          */
/* ------------------------------------------------------------------ */
function SequenceCard({
  item,
  index,
  hasSubmitted,
  isCorrect,
  isInTimeline,
  moveCard,
  onTap,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: {
  item: SequenceItem;
  index: number;
  hasSubmitted: boolean;
  isCorrect?: boolean;
  isInTimeline: boolean;
  moveCard?: (dragIndex: number, hoverIndex: number) => void;
  onTap?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag({
    type: DRAG_TYPE,
    item: () => ({ id: item.id, index, fromTimeline: isInTimeline }),
    canDrag: !hasSubmitted,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: DRAG_TYPE,
    hover(dragged: { id: string; index: number; fromTimeline: boolean }, monitor) {
      if (!ref.current || !isInTimeline || !moveCard) return;
      if (!dragged.fromTimeline) return;
      if (dragged.index === index) return;

      const hoverRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverRect.bottom - hoverRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;
      const hoverClientY = clientOffset.y - hoverRect.top;

      if (dragged.index < index && hoverClientY < hoverMiddleY) return;
      if (dragged.index > index && hoverClientY > hoverMiddleY) return;

      moveCard(dragged.index, index);
      dragged.index = index;
    },
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  });

  drag(drop(ref));

  const borderColor = hasSubmitted
    ? isCorrect
      ? 'border-accent/40 bg-accent/5'
      : 'border-destructive/40 bg-destructive/5'
    : isOver
    ? 'border-primary bg-primary/5 shadow-md'
    : 'border-border/50 bg-card hover:border-primary/50 hover:shadow-md';

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      whileHover={!hasSubmitted ? { scale: 1.01 } : undefined}
      whileTap={!hasSubmitted ? { scale: 0.97 } : undefined}
      className={`flex items-stretch gap-2 px-2 py-2 rounded-xl border-2 transition-all select-none touch-none ${borderColor} ${
        isDragging ? 'opacity-20 scale-90 shadow-xl' : ''
      } ${!hasSubmitted ? 'cursor-move' : ''}`}
    >
      <button
      type="button"
      onClick={onTap}
      disabled={hasSubmitted || !onTap}
      aria-label={isInTimeline ? `Remove ${item.label} from the order` : `Add ${item.label} to the order`}
      className="flex min-w-0 flex-1 items-start gap-3 px-2 py-1 text-left disabled:cursor-default"
      >
      {!hasSubmitted && (
        <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
      )}
      {isInTimeline && (
        <span className={`flex items-center justify-center w-7 h-7 rounded-lg text-xs font-extrabold flex-shrink-0 ${
          hasSubmitted
            ? (isCorrect ? 'bg-accent text-white' : 'bg-destructive text-white')
            : 'bg-primary/10 text-primary'
        }`}>
          {index + 1}
        </span>
      )}
      <span className="flex-1 min-w-0">
        <span className="block font-semibold text-sm leading-tight">{item.label}</span>
        {item.description && (
          <span className="block text-xs text-muted-foreground mt-0.5 leading-snug">{item.description}</span>
        )}
      </span>
      {hasSubmitted && isCorrect !== undefined && (
        isCorrect
          ? <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
          : <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
      )}
      </button>
      {isInTimeline && !hasSubmitted && (
      <div className="flex flex-col gap-1">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onMoveUp?.();
          }}
          disabled={!canMoveUp}
          aria-label={`Move ${item.label} up`}
          className="flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-background text-foreground disabled:opacity-40"
        >
          <ArrowUp className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onMoveDown?.();
          }}
          disabled={!canMoveDown}
          aria-label={`Move ${item.label} down`}
          className="flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-background text-foreground disabled:opacity-40"
        >
          <ArrowDown className="h-4 w-4" />
        </button>
      </div>
      )}
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Timeline drop zone                                                */
/* ------------------------------------------------------------------ */
function TimelineDropZone({
  onDropItem,
  children,
  hasSubmitted,
  isEmpty,
}: {
  onDropItem: (itemId: string) => void;
  children: React.ReactNode;
  hasSubmitted: boolean;
  isEmpty: boolean;
}) {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: DRAG_TYPE,
    drop: (dragged: { id: string; fromTimeline: boolean }) => {
      if (!dragged.fromTimeline) {
        onDropItem(dragged.id);
      }
    },
    canDrop: () => !hasSubmitted,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  return (
    <div
      ref={drop as any}
      className={`min-h-[80px] rounded-2xl border-2 p-3 transition-all touch-none ${
        isOver && canDrop
          ? 'border-primary bg-primary/5 shadow-inner'
          : isEmpty
          ? 'border-dashed border-border/50 bg-muted/10'
          : 'border-border/40 bg-background'
      }`}
    >
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Card bank (unplaced items)                                        */
/* ------------------------------------------------------------------ */
function CardBank({
  items,
  hasSubmitted,
  onReturn,
}: {
  items: SequenceItem[];
  hasSubmitted: boolean;
  onReturn: (itemId: string) => void;
}) {
  const [{ isOver }, drop] = useDrop({
    accept: DRAG_TYPE,
    drop: (dragged: { id: string; fromTimeline: boolean }) => {
      if (dragged.fromTimeline) onReturn(dragged.id);
    },
    canDrop: () => !hasSubmitted,
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  });

  if (items.length === 0 && !hasSubmitted) return null;
  if (items.length === 0) return null;

  return (
    <div
      ref={drop as any}
      className={`space-y-2 p-3 rounded-2xl border-2 border-dashed transition-all touch-none ${
        isOver ? 'border-primary bg-primary/5 shadow-inner' : 'border-border/50 bg-muted/10'
      }`}
    >
      {items.map((item, i) => (
        <SequenceCard
          key={item.id}
          item={item}
          index={i}
          hasSubmitted={hasSubmitted}
          isInTimeline={false}
          onTap={() => handleDropToTimeline(item.id)}
        />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Shuffle helper                                                    */
/* ------------------------------------------------------------------ */
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/* ------------------------------------------------------------------ */
/*  Main Sequencing component                                         */
/* ------------------------------------------------------------------ */
export default function Sequencing({
  slide,
  selectedAnswer,
  onAnswerChange,
  hasSubmitted,
  className = '',
}: ActivitySlideProps) {
  const { content } = slide;
  const { backend, options } = getDndBackend();
  const items: SequenceItem[] = content?.items || [];
  const correctOrder: string[] = content?.correctOrder || items.map((i: SequenceItem) => i.id);
  const sequenceMode: SequenceMode = content?.sequenceMode || 'custom';
  const modeConfig = SEQUENCE_MODE_CONFIG[sequenceMode] || SEQUENCE_MODE_CONFIG.custom;
  const ModeIcon = modeConfig.icon;

  // Timeline = ordered array of item IDs the student has placed
  const timeline: string[] = selectedAnswer || [];

  // Items not yet placed on the timeline
  const unplaced = items.filter((item) => !timeline.includes(item.id));

  // Stable shuffled order for the bank (only shuffle once)
  const [shuffledBank] = useState<SequenceItem[]>(() => shuffle(items));
  const bankItems = shuffledBank.filter((item) => !timeline.includes(item.id));

  // Drop from bank onto timeline
  const handleDropToTimeline = useCallback(
    (itemId: string) => {
      if (timeline.includes(itemId)) return;
      const updated = [...timeline, itemId];
      onAnswerChange(updated);
    },
    [timeline, onAnswerChange]
  );

  // Return from timeline to bank
  const handleReturn = useCallback(
    (itemId: string) => {
      const updated = timeline.filter((id) => id !== itemId);
      onAnswerChange(updated);
    },
    [timeline, onAnswerChange]
  );

  // Reorder within timeline
  const moveCard = useCallback(
    (dragIdx: number, hoverIdx: number) => {
      const updated = [...timeline];
      const [removed] = updated.splice(dragIdx, 1);
      updated.splice(hoverIdx, 0, removed);
      onAnswerChange(updated);
    },
    [timeline, onAnswerChange]
  );

  // Get item by id
  const getItem = (id: string) => items.find((i) => i.id === id);

  // Check correctness per position
  const isPositionCorrect = (idx: number) => timeline[idx] === correctOrder[idx];

  // Count correct
  const correctCount = hasSubmitted
    ? timeline.filter((id, idx) => id === correctOrder[idx]).length
    : 0;

  return (
    <DndProvider backend={backend} options={options}>
      <div className={`w-full ${className}`}>
        <div className="bg-gradient-to-br from-indigo-500/5 via-blue-500/5 to-cyan-500/5 rounded-3xl p-5 sm:p-6 border border-border/40">
          {/* Header */}
          <div className="text-center mb-5">
            <div className={`inline-flex items-center gap-2 ${modeConfig.badgeColor} px-4 py-1.5 rounded-full mb-2`}>
              <ModeIcon className="w-4 h-4" />
              <span className="text-sm font-bold">{modeConfig.label}</span>
            </div>
            {content?.instruction && (
              <p className="text-sm text-muted-foreground">
                {stripHtml(content.instruction)}
              </p>
            )}
            <p className="mt-1 text-xs text-muted-foreground">
              Tap a card to add or remove it. Use the arrows to reorder, or drag cards.
            </p>
            {/* Progress dots */}
            <div className="flex items-center justify-center gap-2 mt-2">
              <div className="flex gap-1.5">
                {items.map((item, i) => (
                  <div key={item.id} className={`w-2.5 h-2.5 rounded-full transition-all ${
                    timeline.includes(item.id) ? 'bg-indigo-500' : 'bg-muted-foreground/20'
                  }`} />
                ))}
              </div>
              <span className="text-xs text-muted-foreground font-medium">
                {timeline.length} of {items.length} placed
              </span>
            </div>
          </div>

          {/* Card bank */}
          {bankItems.length > 0 && (
            <div className="mb-5">
              <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
                <ArrowDown className="w-3.5 h-3.5" />
                Cards to Order
              </p>
              <CardBank
                items={bankItems}
                hasSubmitted={hasSubmitted}
                onReturn={handleReturn}
              />
            </div>
          )}

          {/* Timeline */}
          <div className="mb-5">
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">
              Timeline {timeline.length > 0 && `(${timeline.length}/${items.length})`}
            </p>

            <TimelineDropZone
              onDropItem={handleDropToTimeline}
              hasSubmitted={hasSubmitted}
              isEmpty={timeline.length === 0}
            >
              {timeline.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {modeConfig.dropPlaceholder}
                </p>
              ) : (
                <div className="space-y-2">
                  {timeline.map((id, idx) => {
                    const item = getItem(id);
                    if (!item) return null;
                    return (
                      <div key={id} className="flex items-center gap-2">
                        <SequenceCard
                          item={item}
                          index={idx}
                          hasSubmitted={hasSubmitted}
                          isCorrect={hasSubmitted ? isPositionCorrect(idx) : undefined}
                          isInTimeline
                          moveCard={moveCard}
                          onTap={() => handleReturn(id)}
                          onMoveUp={() => moveCard(idx, idx - 1)}
                          onMoveDown={() => moveCard(idx, idx + 1)}
                          canMoveUp={idx > 0}
                          canMoveDown={idx < timeline.length - 1}
                        />
                        {idx < timeline.length - 1 && (
                          <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0 hidden sm:block" />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </TimelineDropZone>
          </div>

          {/* Results summary */}
          {hasSubmitted && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`rounded-2xl p-4 text-center text-sm font-bold border ${
                correctCount === items.length
                  ? 'bg-accent/10 text-accent border-accent/20'
                  : 'bg-amber-500/10 text-amber-600 border-amber-500/20'
              }`}
            >
              {correctCount === items.length
                ? '✅ Perfect! All items in the correct order!'
                : `${correctCount} of ${items.length} in the correct position.`}
            </motion.div>
          )}

          {/* Show correct order after submission if not perfect */}
          {hasSubmitted && correctCount < items.length && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="rounded-2xl border border-border/40 p-4 bg-card/60 mt-4"
            >
              <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-3">
                Correct Order
              </p>
              <div className="space-y-1.5">
                {correctOrder.map((id, idx) => {
                  const item = getItem(id);
                  if (!item) return null;
                  return (
                    <div key={id} className="flex items-center gap-2 text-sm">
                      <span className="flex items-center justify-center w-6 h-6 rounded-lg bg-accent/10 text-accent text-xs font-bold flex-shrink-0">
                        {idx + 1}
                      </span>
                      <span className="font-medium">{item.label}</span>
                      {item.description && (
                        <span className="text-muted-foreground">— {item.description}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </DndProvider>
  );
}
